import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LuShieldX, LuPlus, LuTrash2 } from "react-icons/lu";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";

export function DisposableDomainsPage() {
  const qc = useQueryClient();
  const [domain, setDomain] = useState("");
  const [reason, setReason] = useState("");
  const list = useQuery({ queryKey: ["console", "admin", "disposable"], queryFn: () => api.adminDisposableDomains.list() });
  const add = useMutation({
    mutationFn: () => api.adminDisposableDomains.add({ domain, reason }),
    onSuccess: () => {
      toast.success(`${domain} blocked.`);
      setDomain("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["console", "admin", "disposable"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Failed."),
  });
  const remove = useMutation({
    mutationFn: (d: string) => api.adminDisposableDomains.remove(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["console", "admin", "disposable"] }),
  });
  return (
    <>
      <PageHeader
        icon={LuShieldX}
        title="Disposable blocklist"
        description="Domains added here block sign-ups + custom-domain claims. Static shared list still applies in addition."
      />
      <div className="p-8 space-y-6">
        <Card>
          <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
            <LuPlus className="w-4 h-4 text-[var(--color-text-subtle)]" />
            <p className="text-sm font-semibold text-[var(--color-text)]">Add domain</p>
          </div>
          <div className="p-5 flex flex-wrap items-end gap-3">
            <input
              type="text"
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] font-mono w-64 focus:outline-none focus:border-[var(--color-accent)]"
            />
            <input
              type="text"
              placeholder="Why blocked"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] flex-1 max-w-md focus:outline-none focus:border-[var(--color-accent)]"
            />
            <Button onClick={() => add.mutate()} disabled={!domain || add.isPending}>
              <LuPlus className="w-4 h-4" />
              Block
            </Button>
          </div>
        </Card>

        <Card>
          <div className="divide-y divide-[var(--color-border)]">
            {list.data?.domains.length === 0 && (
              <p className="px-5 py-6 text-sm text-[var(--color-text-muted)]">
                No domains in the runtime blocklist (static list still active).
              </p>
            )}
            {list.data?.domains.map((d) => (
              <div key={d.domain} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-mono text-[var(--color-text)]">{d.domain}</p>
                  <p className="text-[11px] text-[var(--color-text-subtle)] truncate">
                    {d.reason ?? "—"}{" "}
                    {d.added_by && <>· {d.added_by}</>}{" "}
                    · {new Date(d.added_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => remove.mutate(d.domain)}
                  className="text-[var(--color-text-subtle)] hover:text-[var(--color-error)] transition-colors p-1"
                >
                  <LuTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
